import { twMerge } from "tailwind-merge";
import { motion } from "motion/react";

export type Props = {
  /** Content to animate */
  children: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Animation duration in seconds */
  duration?: number;
  /** Initial width (default: 0) */
  initialWidth?: string | number;
  /** Target width to animate to (default: "auto") */
  animateWidth?: string | number;
  /** Initial opacity (default: 0) */
  initialOpacity?: number;
  /** Target opacity (default: 1) */
  animateOpacity?: number;
  /** HTML element to render as */
  as?: "div" | "section" | "aside" | "main";
  /** Test ID for the element */
  testId?: string;
};

/**
 * A panel reveal animation that animates width and opacity.
 * Used for revealing side panels in two-panel layouts.
 *
 * @example
 * // Reveal from 0 to 40%
 * <MotionPanelReveal initialWidth={0} animateWidth="40%">
 *   Content
 * </MotionPanelReveal>
 *
 * @example
 * // Contract from 100% to 60%
 * <MotionPanelReveal initialWidth="100%" animateWidth="60%" initialOpacity={1}>
 *   Content
 * </MotionPanelReveal>
 */
export function MotionPanelReveal({
  children,
  className,
  duration = 0.4,
  initialWidth = 0,
  animateWidth = "auto",
  initialOpacity = 0,
  animateOpacity = 1,
  as = "div",
  testId = "motion-panel-reveal",
  ...props
}: Props) {
  const Component = motion[as];

  return (
    <Component
      initial={{ width: initialWidth, opacity: initialOpacity }}
      animate={{ width: animateWidth, opacity: animateOpacity }}
      exit={{ width: initialWidth, opacity: initialOpacity }}
      transition={{
        duration,
        ease: [0.4, 0, 0.2, 1], // ease-out cubic
      }}
      className={twMerge("overflow-hidden", className)}
      data-testid={testId}
      {...props}
    >
      {children}
    </Component>
  );
}

export default MotionPanelReveal;
